import { forwardRef, Inject, Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { AuthenticationService } from 'app/auth/service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  of,
  Observable,
  throwError,
  forkJoin,
  combineLatest,
  isObservable,
  from,
} from 'rxjs';
import {
  switchMap,
  take,
  tap,
  catchError,
  map,
  withLatestFrom,
  mergeMap,
} from 'rxjs/operators';
import { BejovoSzamla, Column, Partner, Penznem } from '../models/torzsek';
import { findByProp, parseJSON, stringifyIfJSON } from '../utils';
import { PartnerekService } from './partnerek.service';
import { CikktorzsService } from './cikktorzs.service';
import { PenznemekService } from './penznemek.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { KimenoSzamlaService } from './kimeno-szamla.service';
import { find } from 'lodash';
import { KimenoSzamlaKifizetesComponent } from '../pages/kimeno-szamla-kifizetes/kimeno-szamla-kifizetes.component';
import { FizetesiModokService } from './fizetesi-modok.service';

@Injectable({
  providedIn: 'root',
})
export class BejovoSzamlaService {
  private readonly apiEndpoint = 'bejovoszamla';
  private readonly columnsEndpoint = 'bejovoszamla/columns';
  private readonly listaEndpoint = 'service/helplib/oldal_lista/be';
  private readonly updateEndpoint = 'service/helplib/bizonylat_frissites/be';
  private readonly nyomtatasBeallitasEndpoint = 'service/helplib/nyomtatas_kapcsolo/be';
  private readonly kifizetesHozzaadasEnpoint = 'service/bizonylatlib/bejovo_fizetve';
  private readonly kifizetesVisszaEnpoint = 'service/bizonylatlib/bejovo_nincs_fizetve';
  private readonly koltseghelyEndpoint = 'service/torzslib/tetel_koltseghely';

  private lastFetch: Date = new Date();
  private bejovoszamla = JSON.parse(localStorage.getItem('bejovoszamla'));

  private readonly _data = new BehaviorSubject<any[]>(null);
  private readonly _selectedData = new BehaviorSubject<any>(null);
  public data: any[] = [];
  private pageNum: number = 1;
  public readonly streamEnd = new BehaviorSubject<boolean>(false);
  private raktarId = JSON.parse(localStorage.getItem('currentUser'))?.raktar;

  // private state$ = combineLatest([
  //   this._data,
  //   this.partnerekService.data$,
  //   this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
  //   this.penznemekService.data$,
  // ]).pipe(
  //   switchMap(([state, partnerek, cikkek, penznemek]) => {
  //     return state ? of(this.mapBejovoSzamlak(state, partnerek, cikkek, penznemek)) : this.bejovoszamla?.db > 0 ? this.getDBData() : this.fetchData()
  //   })
  // );

  private state$ = combineLatest([
    this._data,
    this.partnerekService.data$,
    //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
    this.penznemekService.data$,
    this.api.get('kimenoszamla'),
    this.fizetesiModokService.data$
  ]).pipe(
    switchMap(([state, partnerek, penznemek, kimenoszamlak, fizetesimodok]) =>
      state ? of(this.mapBejovoSzamlak(state, partnerek, penznemek, kimenoszamlak, fizetesimodok)) : this.fetchData()
    )
  );

  public data$ = combineLatest([
    this.state$,
    this.authService.currentUser$
  ])
  .pipe(map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  );

  constructor(
    private authService: AuthenticationService,
    private partnerekService: PartnerekService,
    private cikkService: CikktorzsService,
    private penznemekService: PenznemekService,
    private api: ApiService,
    private stateService: StateService,
    private dbService: NgxIndexedDBService,
    private fizetesiModokService: FizetesiModokService
  ) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  // get initData$(): Observable<BejovoSzamla[]> {
  //   return this._data.pipe(
  //     take(1),
  //     switchMap((state: BejovoSzamla[]) =>
  //       state
  //         ? forkJoin([
  //           of(state), 
  //           this.partnerekService.initData$, 
  //           this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
  //           this.penznemekService.initData$])
  //           .pipe(
  //             map(([kimenoSzamlak, partnerek, cikkek, penznemek]) =>
  //               this.mapBejovoSzamlak(kimenoSzamlak, partnerek, cikkek, penznemek)
  //             )
  //           )
  //         : JSON.parse(localStorage.getItem('bejovoszamla'))?.db > 0 ? this.getDBData() : this.fetchData()
  //     ),
  //     withLatestFrom(this.authService.currentUser$),
  //     map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
  //   );
  // }

  get initData$(): Observable<BejovoSzamla[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: BejovoSzamla[]) =>
        state
          ? forkJoin([
            of(state), 
            this.partnerekService.initData$, 
            //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
            this.penznemekService.initData$,
            this.api.get('kimenoszamla'),
            this.fizetesiModokService.initData$
          ])
            .pipe(
              map(([bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok]) =>
                this.mapBejovoSzamlak(bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok)
              )
            )
          : this.fetchData()
      ),
      withLatestFrom(this.authService.currentUser$),
      map(([data, user]) => data.filter((e: any) => e.oe_id === user.oe))
    );
  }

  add(data: BejovoSzamla, sztornozhato: any): Observable<BejovoSzamla> {
    return this.api.post(this.apiEndpoint, this.toJSON(data)).pipe(
      tap((added: BejovoSzamla) => {
        this.stateService.setStateOnAdd(
          {...added,
           sztornozhato: sztornozhato}, this._data);

        // this.dbService.add('bejovoszamla', {...added,
        //   sztornozhato: sztornozhato}).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<BejovoSzamla> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((storno: BejovoSzamla) => {
        this.stateService.setStateOnDelete(storno, this._data);
        //this.dbService.update('bejovoszamla', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  getById(id: string): Observable<BejovoSzamla> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        // Find the data
        const data =
          state.find((item: BejovoSzamla) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  private fetchDataById(id: number): Observable<any> {
    return this.api.get(`${this.apiEndpoint}/${id}`).pipe(
      catchError((err: any) => {
        console.log(err);
        return of(null);
      })
    );
  }

  updateBejovoSzamlaRow(id: number, helyesbitoId?: number): Observable<any> {
    return this.fetchDataById(id).pipe(
      tap((bejovoSzamla) => {
        const mappedRow = {
          ...bejovoSzamla,
          fajl: true 
        }
        this.stateService.setStateOnUpdate(mappedRow, this._data);
      })
    );
  }

  fetchAndMapDataByIdKifizetes(id: number | string): Observable<any> {
    return forkJoin([
      this.api.get(`${this.apiEndpoint}/${id}`).pipe(
        catchError((err: any) => {
          console.log(err);
          return of(null);
        })
      ),
      this.partnerekService.initData$,
      this.penznemekService.initData$,
      this.fizetesiModokService.initData$
    ]).pipe(
      map(([bejovoSzamla, partnerek, penznemek, fizetesimodok]) => {
        return this.mapBejovoSzamla(bejovoSzamla, partnerek, penznemek, fizetesimodok)
      })
    );
  }

  fetchAndMapDataById(id: number | string): Observable<any> {
    return forkJoin([
      this.api.get(`${this.apiEndpoint}/${id}`).pipe(
        catchError((err: any) => {
          console.log(err);
          return of(null);
        })
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$,
      this.fizetesiModokService.initData$
    ]).pipe(
      map(([bejovoSzamla, partnerek, penznemek, fizetesimodok]) => {
        const retVal = this.mapBejovoSzamla(bejovoSzamla, partnerek, penznemek, fizetesimodok)
        this.stateService.setStateOnUpdate(retVal, this._data);
        this._selectedData.next(retVal);
        return retVal;
      }
      )
    );
  }

  update(data: BejovoSzamla, sztornozhato: any): Observable<BejovoSzamla> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        tap((updated: BejovoSzamla) => {
          this.stateService.setStateOnUpdate(
            {...updated,
             sztornozhato: sztornozhato
            }, this._data);
        }),
        catchError((err: any) => throwError(err))
      );
  }

  updateOnKoltseghely(data: BejovoSzamla, sztornozhato: any): Observable<BejovoSzamla> {
    return this.api
      .put(`${this.apiEndpoint}/${data.id}`, this.toJSON(data))
      .pipe(
        tap((updated: any) => {
          this.stateService.setStateOnUpdate(
            {...updated,
            tetelek: updated.tetelek.map((t: any) => ({ ...t, koltseghelyek: parseJSON(t.koltseghelyek) })),
             sztornozhato: sztornozhato
            }, this._data);
        }),
        catchError((err: any) => throwError(err))
      );
  }

  getBizonylatValtozas(): any {
    this.lastFetch = new Date();
    const oe = JSON.parse(localStorage.getItem('currentUser')).oe;
    const last = JSON.parse(localStorage.getItem('bejovoszamla'))?.lastFetch;
    return this.api.postBizonylat(`${this.updateEndpoint}/${oe}/${last}`, "")
           .pipe(
            take(1),
            tap(valtozas => {
              if (this._data.value.length == 0) {
                //this.fetchData().subscribe();
              } else {
                if (valtozas.valtozasok.length > 0) {
                  valtozas.valtozasok.forEach((bizonylat) => {
                    const biz = findByProp(this._data.value, "id", bizonylat.id);
                    if (biz) {
                      this.stateService.setStateOnUpdate(bizonylat, this._data);
                    } else {
                      this.stateService.setStateOnAdd(bizonylat, this._data);
                    }
                  });
                }
                if (valtozas.torolt_lista.length > 0) {
                  valtozas.torolt_lista.forEach((bizonylat) => {
                    const biz = findByProp(
                      this._data.value,
                      "id",
                      bizonylat.modul_id
                    );
                    if (biz) {
                      this.stateService.setStateOnDelete(biz, this._data);
                    }
                  });
                }
              }
    
              localStorage.setItem(
                "bejovoszamla",
                JSON.stringify({
                  db: this._data.value.length,
                  lastFetch: this.lastFetch,
                })
              );
            }))
  }

  fetchData(): Observable<BejovoSzamla[]> {
    this.lastFetch = new Date();
    return forkJoin([
      this.api.get(`${this.apiEndpoint}`).pipe(
        // bufferCount(25),
        // concatMap(objs => of(objs)),
        tap((data: any[]) => {
          this._data.next(data);
        }),
        catchError((err: any) => {
          console.log(err);
          return of([]);
        })
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$,
      this.api.get('kimenoszamla'),
      this.fizetesiModokService.initData$
    ]).pipe(
      map(([bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok ]) => {
        localStorage.setItem('bejovoszamla', JSON.stringify({db: bejovoSzamlak.length, lastFetch: this.lastFetch}));
        return this.mapBejovoSzamlak(bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok)
      })
    );
  }

  getDBData(): Observable<any> {
    console.log('get bejovoszamlak from indexedDB')
    // return this.dbService.getAll('nyugta').pipe(
    //   tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
    // );
    return forkJoin([
      this.dbService.getAll('bejovoszamla').pipe(
         tap((data: any[]) => this._data.next(data.sort((a,b) => b.id - a.id))),
      ),
      this.partnerekService.initData$,
      //this.cikkService.getElerhetoKeszlet(JSON.parse(localStorage.getItem('currentUser'))?.raktar),
      this.penznemekService.initData$,
      this.api.get('kimenoszamla'),
      this.fizetesiModokService.initData$
      ]).pipe(
      map(([bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok]) => {
        return this.mapBejovoSzamlak(bejovoSzamlak, partnerek, penznemek, kimenoszamlak, fizetesimodok)
      })
    );
  }

  private getColumns(): Observable<Column[]> {
      return this.api.get(this.columnsEndpoint).pipe(
        map((columns: Column[]) => {
          const mappedColumns = [
          ...columns,
          { name: 'rogzites_datuma', nullable: false},
          { name: 'szamlasorszam', title: "Szamla sorszám", nullable: true},
          { name: 'tetelek', nullable: true },
        ];
        localStorage.setItem('bejovo-szamla_columns', JSON.stringify(mappedColumns));
        return mappedColumns;
        }),
        catchError((err: any) => {
          console.log(err);
  
          return of([]);
        })
      );
  }

  private mapBejovoSzamlak(
    bejovoSzamlak: BejovoSzamla[],
    partnerek: Partner[],
    //cikkek: any[],
    penznemek: any[],
    kimenoszamlak: any[],
    fizetesimodok: any[],
  ): BejovoSzamla[] {
    return bejovoSzamlak.map((e: any) => {
      const penznemObj: Penznem = findByProp(
        penznemek,
        'id',
        e.penznem_id
      );
  
      const penznem = penznemObj?.outfix || penznemObj?.prefix;
      const fizetendo = this.calcFizetendo(bejovoSzamlak, e.id);
      const isFizetve: boolean = e.statusz == 2 || Math.abs(fizetendo) <= Math.abs(e.fizetett);
      //const generalt = this.checkSzamlasorszam(kimenoszamlak, e)

      let sztornozhato = true;
      return {
        ...e,
        partner: findByProp(partnerek, 'id', e.partner_id)?.nev,
        szamla_kelte: new Date(e.szamla_kelte),
        penznem,
        fizetendo,
        fizetesi_mod_text: findByProp(fizetesimodok, 'id', e.fizetesi_mod_id)?.nev,
        fizetve: isFizetve,
        teljesites_datum: new Date(e.teljesites_datum),
        fizetesi_hatarido: new Date(e.fizetesi_hatarido),
        rogzites_datuma: e.rogzites_datuma ? new Date(e.rogzites_datuma) : null,
        sztornozhato: sztornozhato,
        szin: e.generalt && !e.elfogadva ? 'orange' : '',
        //tetelek: e.tetelek.map((t: any) => ({ ...t, koltseghelyek: parseJSON(t.koltseghelyek || []) })),
      }
    });
  }

  private mapBejovoSzamla(
    bejovoSzamla: any,
    partnerek: Partner[],
    penznemek: any[],
    fizetesimodok: any[],
  ): BejovoSzamla[] {
      const penznemObj: Penznem = findByProp(
        penznemek,
        'id',
        bejovoSzamla.penznem_id
      );
  
      const penznem = penznemObj?.outfix || penznemObj?.prefix;
      const fizetendo = this.calcFizetendoById(bejovoSzamla);
      const isFizetve: boolean = bejovoSzamla.statusz == 2 || Math.abs(fizetendo) <= Math.abs(bejovoSzamla.fizetett);

      //const generalt = this.checkSzamlasorszam(kimenoszamlak, bejovoSzamla)

      let sztornozhato = true;
      return {
        ...bejovoSzamla,
        partner: findByProp(partnerek, 'id', bejovoSzamla.partner_id)?.nev,
        szamla_kelte: new Date(bejovoSzamla.szamla_kelte),
        penznem,
        fizetendo,
        fizetve: isFizetve,
        fizetesi_mod_text: findByProp(fizetesimodok, 'id', bejovoSzamla.fizetesi_mod_id)?.nev,
        teljesites_datum: new Date(bejovoSzamla.teljesites_datum),
        fizetesi_hatarido: new Date(bejovoSzamla.fizetesi_hatarido),
        rogzites_datuma: bejovoSzamla.rogzites_datuma ? new Date(bejovoSzamla.rogzites_datuma) : null,
        sztornozhato: sztornozhato,
        szin: bejovoSzamla.generalt && !bejovoSzamla.elfogadva ? 'orange' : '',
        tetelek: bejovoSzamla.tetelek.map((t: any) => ({ ...t, penznem, koltseghelyek: parseJSON(t.koltseghelyek) })),
      }
  }

  private calcFizetendo(bejovoSzamlak: any[], id: number): number {
    const bejovoSzamla = findByProp(bejovoSzamlak, 'id', id);

    if(bejovoSzamla.skonto_ok) {
      const skontoValue = bejovoSzamla.brutto_ar * (1 - (bejovoSzamla.skonto / 100))
      return bejovoSzamla?.penznem_id == 1 ? Math.round(skontoValue) : skontoValue;
    }

    return bejovoSzamla.brutto_ar;
  }

  private calcFizetendoById(bejovoSzamla: any): number {
    if(bejovoSzamla.skonto_ok) {
      const skontoValue = bejovoSzamla.brutto_ar * (1 - (bejovoSzamla.skonto / 100))
      return bejovoSzamla?.penznem_id == 1 ? Math.round(skontoValue) : skontoValue;
    }

    return bejovoSzamla.brutto_ar;
  }

  private toJSON(data: any): BejovoSzamla {
    return {
      ...data,
      rogzites_datuma: new Date(new Date(data.rogzites_datuma).setHours(new Date(data.rogzites_datuma).getHours() + 3)),
      teljesites_datum: new Date(new Date(data.teljesites_datum).setHours(new Date(data.teljesites_datum).getHours() + 3)),
      fizetesi_hatarido: new Date(new Date(data.fizetesi_hatarido).setHours(new Date(data.fizetesi_hatarido).getHours() + 3)),
      szamla_kelte: new Date(new Date(data.szamla_kelte).setHours(new Date(data.szamla_kelte).getHours() + 3)),
      cim: stringifyIfJSON(data.cim),
      tetelek: data.tetelek.map((t: any) => ({ ...t, koltseghelyek: stringifyIfJSON(t.koltseghelyek) })),
    };
  }

  public updateKoltsegHelyRow(row_id: number, koltseghely: any): Observable<any> {
    const req = this.api.put(
      `${this.koltseghelyEndpoint}`,
      {
        sor_id: row_id,
        koltseghely: stringifyIfJSON(koltseghely),
      },
      'disable'
    );

    const req$ = isObservable(req)
      ? req
      : req && typeof (req as any).then === 'function'
        ? from(req)                  // Promise
        : of(req);                   // string/void/etc.

    return req$;
  }
  
  // public updateKoltsegHelyRow(row_id: number, koltseghely: any): Observable<any> {
  //   return this.api
  //   .put(`${this.koltseghelyEndpoint}`, {
  //     sor_id: row_id,
  //     koltseghely: stringifyIfJSON(koltseghely)
  //   }, 'disable')
  //   .pipe(
  //     catchError((err: any) => throwError(err))
  //   );
  // }

  addKifizetes(id: any): any {
    return this.api
    .postBizonylat(`${this.kifizetesHozzaadasEnpoint}/${id}`, '')
    .pipe(
      tap((updated: any) => {
        //this.stateService.setStateOnUpdate(updateRow, this._data);
        //this.getBizonylatValtozas();
      }),
      catchError(() => of(null))
    );
  }

  removeKifizetes(id: any) {
    return this.api
    .postBizonylat(`${this.kifizetesVisszaEnpoint}/${id}`, '')
    .pipe(
      tap((updated: any) => {
        //this.stateService.setStateOnUpdate(updateRow, this._data);
        //this.getBizonylatValtozas();
      }),
      catchError(() => of(null))
    );
  }

  generatePdf(payload: { id: number; sztorno?: boolean }): Observable<any> {
    return this.api
      .postBizonylat(`${environment.pdfGeneralas}/${this.apiEndpoint}`, payload)
      .pipe(
        map(({ url }) => `${environment.uploads}${url}`),
        catchError(() => of(null))
      );
  }

  sztorno(id: number): Observable<any> {
    return this.api.delete(this.apiEndpoint, id, 'sztornózás').pipe(
      tap((storno: any) => {
        this.stateService.setStateOnUpdate(storno, this._data);
        //this.dbService.update('bejovoszamla', storno).subscribe();
      }),
      catchError((err: any) => throwError(err))
    );
  }

  updateState(data: any[]): void{
    data.forEach(item => {
      this.stateService.setStateOnUpdate(item, this._data);
    })
  }

  getMennyiseg(): any {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));

    return this.api.postBizonylat(`service/helplib/ossz_bizonylat/be/${currentUser.id}`, "");
  }

  clearData(): any {
    this.data = [];
  }

  nyomtatasBeallitas(row: any, megjegyzes_nyomtat: number, hivatkozas_nyomtat: number = 0): Observable<any> {
    return this.api
    .post(`${this.nyomtatasBeallitasEndpoint}/${row.id}/${megjegyzes_nyomtat}/${hivatkozas_nyomtat}`, '')
    .pipe(
      tap((updated: any) => {
        const updateRow = {
          ...row,
          megjegyzes_nyomtat: megjegyzes_nyomtat,
        }
        this.stateService.setStateOnUpdate(updateRow, this._data);
      }),
      catchError(() => of(null))
    );
  }

  checkSzamlasorszam(kimenoszamlak: any[], e: any): boolean {
    const generalt = kimenoszamlak.find(kimeno => kimeno.bizonylat_szam === e.szamlasorszam);
  
    if (e.szamlasorszam?.endsWith('/S')) {
      const baseSzamlasorszam = e.szamlasorszam.slice(0, -2);
  
      const foundKimenoWithBase = kimenoszamlak.find(
        kimeno => kimeno.bizonylat_szam === baseSzamlasorszam
      );
  
      return !!foundKimenoWithBase;
    }
  
    return !!generalt;
  }
}
